import React, { useState } from 'react';
import {
  ActivityIndicator,
  Keyboard,
  Linking,
  Modal,
  Platform,
  Pressable,
  Share,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { KeyboardAvoidingContainer } from '../layout/KeyboardAvoidingContainer';
import { dismissKeyboardThen, numberPadInputProps } from '../layout/keyboard';

import { useHouseholdMembership } from '../../contexts/HouseholdMembershipContext';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { createHouseholdInvite } from '../../services/householdService';
import { GlassButton } from '../ui/GlassButton';
import { GlassCard } from '../ui/GlassCard';

type Props = {
  visible: boolean;
  onClose: () => void;
};

type DeliveryInfo = {
  sent: boolean;
  skippedReason?: string;
  error?: string;
};

async function copyText(text: string): Promise<boolean> {
  try {
    if (Platform.OS === 'web') {
      const nav = globalThis as {
        navigator?: { clipboard?: { writeText: (v: string) => Promise<void> } };
      };
      await nav.navigator?.clipboard?.writeText(text);
      return true;
    }
    await Share.share({ message: text });
    return true;
  } catch {
    return false;
  }
}

export function InviteHouseholdModal({ visible, onClose }: Props) {
  const { refresh } = useHouseholdMembership();
  const [channel, setChannel] = useState<'email' | 'sms' | 'link'>('link');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joinUrl, setJoinUrl] = useState<string | null>(null);
  const [delivery, setDelivery] = useState<DeliveryInfo | null>(null);
  const [copied, setCopied] = useState(false);

  const resetResult = () => {
    setJoinUrl(null);
    setDelivery(null);
    setCopied(false);
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    resetResult();
    try {
      const res = await createHouseholdInvite({
        channel,
        to: channel === 'link' ? undefined : to.trim(),
      });
      setJoinUrl(res.invite.joinUrl);
      if (res.delivery) {
        setDelivery({
          sent: res.delivery.sent,
          skippedReason: res.delivery.skippedReason,
          error: res.delivery.error,
        });
      }
      await refresh();

      if (channel === 'link' && res.invite.joinUrl) {
        try {
          await Share.share({
            message: `Join my MoneyLens household: ${res.invite.joinUrl}`,
            url: res.invite.joinUrl,
          });
        } catch {
          // User dismissed share sheet.
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invite failed');
    } finally {
      setBusy(false);
    }
  };

  const shareLink = async () => {
    if (!joinUrl) return;
    try {
      await Share.share({
        message: `Join my MoneyLens household: ${joinUrl}`,
        url: joinUrl,
      });
    } catch {
      // ignore
    }
  };

  const shareWhatsApp = async () => {
    if (!joinUrl) return;
    const text = encodeURIComponent(`Join my MoneyLens household: ${joinUrl}`);
    const url = `https://wa.me/?text=${text}`;
    try {
      const can = await Linking.canOpenURL(url);
      if (can) {
        await Linking.openURL(url);
        return;
      }
    } catch {
      // fall through
    }
    await shareLink();
  };

  const onCopy = async () => {
    if (!joinUrl) return;
    const ok = await copyText(joinUrl);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const deliveryHint = (() => {
    if (!delivery || channel === 'link') return null;
    if (delivery.sent) {
      return channel === 'email'
        ? 'Email sent. You can still share the link below.'
        : 'SMS sent. You can still share the link below.';
    }
    if (delivery.error) {
      return `Could not send ${channel}: ${delivery.error}. Share the link instead.`;
    }
    if (delivery.skippedReason?.includes('BUDGET_INVITE_FROM_EMAIL')) {
      return 'Email delivery is not configured on the server yet. Share the link instead.';
    }
    if (delivery.skippedReason) return delivery.skippedReason;
    return null;
  })();

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <KeyboardAvoidingContainer>
        <Pressable
          style={styles.backdrop}
          onPress={() => dismissKeyboardThen(onClose)}
        >
          <Pressable style={styles.sheet} onPress={(e) => e.stopPropagation()}>
            <GlassCard>
            <Text style={styles.title}>Invite to household</Text>
            <Text style={styles.sub}>
              Share with your partner. Each person keeps a Mine view; Household combines what you
              both upload.
            </Text>

            <View style={styles.channels}>
              {(['link', 'email', 'sms'] as const).map((c) => (
                <Pressable
                  key={c}
                  onPress={() => {
                    setChannel(c);
                    resetResult();
                  }}
                  style={[styles.channel, channel === c && styles.channelActive]}
                >
                  <Text style={[styles.channelText, channel === c && styles.channelTextActive]}>
                    {c === 'link' ? 'Link' : c === 'email' ? 'Email' : 'SMS'}
                  </Text>
                </Pressable>
              ))}
            </View>

            {channel !== 'link' ? (
              <TextInput
                value={to}
                onChangeText={setTo}
                placeholder={channel === 'email' ? 'partner@email.com' : '+1…'}
                placeholderTextColor={colors.textMuted}
                autoCapitalize="none"
                onSubmitEditing={() => Keyboard.dismiss()}
                {...(channel === 'email'
                  ? { keyboardType: 'email-address' as const, returnKeyType: 'done' as const, blurOnSubmit: true }
                  : numberPadInputProps('phone'))}
                style={styles.input}
              />
            ) : null}

            {error ? <Text style={styles.error}>{error}</Text> : null}
            {deliveryHint ? <Text style={styles.hint}>{deliveryHint}</Text> : null}
            {joinUrl ? (
              <View style={styles.linkBox}>
                <Text style={styles.linkLabel}>Invite link</Text>
                <Text style={styles.link} selectable>
                  {joinUrl}
                </Text>
                <GlassButton variant="primary" fullWidth onPress={() => void shareLink()}>
                  Share…
                </GlassButton>
                <View style={styles.shareRow}>
                  <GlassButton
                    variant="secondary"
                    style={styles.shareHalf}
                    onPress={() => void shareWhatsApp()}
                  >
                    WhatsApp
                  </GlassButton>
                  <GlassButton
                    variant="secondary"
                    style={styles.shareHalf}
                    onPress={() => void onCopy()}
                  >
                    {copied ? 'Copied' : 'Copy link'}
                  </GlassButton>
                </View>
              </View>
            ) : null}

            <GlassButton
              variant="primary"
              fullWidth
              disabled={busy || (channel !== 'link' && !to.trim())}
              onPress={() => void submit()}
              style={{ marginTop: spacing.md }}
            >
              {busy ? (
                <ActivityIndicator color={colors.text} />
              ) : channel === 'link' ? (
                'Create & share'
              ) : channel === 'email' ? (
                'Send email invite'
              ) : (
                'Send SMS invite'
              )}
            </GlassButton>

            <GlassButton variant="ghost" fullWidth onPress={onClose} style={{ marginTop: spacing.sm }}>
              Close
            </GlassButton>
          </GlassCard>
        </Pressable>
      </Pressable>
      </KeyboardAvoidingContainer>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(2, 6, 23, 0.72)',
    justifyContent: 'center',
    padding: spacing.lg,
  },
  sheet: { maxWidth: 440, width: '100%', alignSelf: 'center' },
  title: { ...typography.headline, fontSize: 20, color: colors.text },
  sub: { ...typography.body, fontSize: 14, color: colors.textMuted, marginTop: spacing.sm },
  channels: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.md },
  channel: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  channelActive: {
    borderColor: 'rgba(61, 214, 164, 0.5)',
    backgroundColor: 'rgba(61, 214, 164, 0.14)',
  },
  channelText: { ...typography.caption, fontWeight: '600', color: colors.textMuted },
  channelTextActive: { color: colors.accent },
  input: {
    marginTop: spacing.md,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    borderRadius: radii.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    color: colors.text,
    ...typography.body,
  },
  error: { ...typography.caption, color: '#F87171', marginTop: spacing.sm },
  hint: { ...typography.caption, color: colors.warning, marginTop: spacing.sm },
  linkBox: { marginTop: spacing.md, gap: spacing.sm },
  linkLabel: { ...typography.caption, color: colors.textMuted },
  link: { ...typography.caption, color: colors.accent },
  shareRow: { flexDirection: 'row', gap: spacing.sm },
  shareHalf: { flex: 1 },
});
